/**
 * Shared numeric formatters for the WAVE Brand DSP.
 *
 * Why this module exists
 * ----------------------
 * In iter-69 a demo showed `$1256K` instead of `$1.3M` because the
 * `(n/1000).toFixed(0) + 'K'` pattern was hand-rolled in four separate
 * files with no M-rollup branch. The fourth copy is the diagnosis —
 * the second was the warning. This module is the single canonical
 * place for money / reach formatting so the next consumer inherits
 * the M / B rollup automatically.
 *
 * Contract
 * --------
 * - `formatMoney(n)` always returns a string with a leading `$`.
 *   Falsy / non-positive → `$0`. Magnitudes auto-roll into B / M / K
 *   so we never emit four-digit-K like `$1256K`. Rounds K to integer,
 *   M and B to one decimal — matching the bands a brand rep cares about.
 * - `formatReach(n)` is the same shape without the `$`. Falsy → `—`
 *   so the UI naturally hides empty reach without each call site
 *   having to branch.
 *
 * Anti-patterns this exists to prevent (do NOT reintroduce):
 *   `$${(n / 1000).toFixed(0)}K`              ← no M rollup; breaks at $1M+
 *   `if (n >= 1000) return '$' + Math.round(n/1000) + 'K'`  ← same
 *   any new hand-rolled currency / reach formatter in any component
 */

/** Money — `$1.3M`, `$120K`, `$5` … never `$1256K`. */
export function formatMoney(n) {
  if (!n || n <= 0) return '$0';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${Math.round(abs / 1e3)}K`;
  return `${sign}$${Math.round(abs)}`;
}

/** Reach — `1.3M`, `120K`, `5` … falsy returns `—` (em-dash). */
export function formatReach(n) {
  if (!n || n <= 0) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(abs / 1e3).toFixed(0)}K`;
  return `${Math.round(abs)}`;
}

export default { formatMoney, formatReach };
